import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { IntakeSubmission } from '@/api/intake/intakeApi.types';
import { Button } from '@/components/ui/button';
import { INTAKE_SUBMISSION_STATUS } from '@/constants/intake';
import {
  useAcceptIntakeSubmissionMutation,
  useReturnIntakeSubmissionMutation,
} from '@/hooks/useIntake';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { ReturnIntakeDialog } from './ReturnIntakeDialog';

interface IntakeReviewActionsProps {
  submission: IntakeSubmission;
  onAccepted?: (acceptedSubmission: IntakeSubmission) => void;
}

export function IntakeReviewActions({ submission, onAccepted }: IntakeReviewActionsProps) {
  const { t } = useTranslation(['intake', 'common']);
  const acceptMutation = useAcceptIntakeSubmissionMutation();
  const returnMutation = useReturnIntakeSubmissionMutation();
  const [isReturning, setIsReturning] = useState(false);

  if (submission.status !== INTAKE_SUBMISSION_STATUS.submitted) {
    return null;
  }

  const isBusy = acceptMutation.isPending || returnMutation.isPending;

  function accept() {
    acceptMutation.mutate(
      { submissionId: submission.id },
      {
        onSuccess: function done(acceptedSubmission) {
          showSuccessNotification(t('intake:reviewSection.accepted'));
          if (onAccepted !== undefined) {
            onAccepted(acceptedSubmission);
          }
        },
        onError: function fail(error) {
          const message = resolveApiErrorMessage(error);
          showErrorNotification(message.length > 0 ? message : t('intake:reviewSection.acceptError'));
        },
      }
    );
  }

  function returnForCorrection(reviewNote: string) {
    returnMutation.mutate(
      { submissionId: submission.id, reviewNote },
      {
        onSuccess: function done() {
          showSuccessNotification(t('intake:reviewSection.returned'));
          setIsReturning(false);
        },
        onError: function fail(error) {
          const message = resolveApiErrorMessage(error);
          showErrorNotification(message.length > 0 ? message : t('intake:reviewSection.returnError'));
        },
      }
    );
  }

  return (
    <>
      <Button
        variant="destructiveGhost"
        size="chip"
        className="border border-zooborder"
        disabled={isBusy}
        onClick={function openReturn() {
          setIsReturning(true);
        }}
      >
        {t('intake:reviewSection.return')}
      </Button>
      <Button
        variant="primary"
        size="chip"
        disabled={isBusy}
        isLoading={acceptMutation.isPending}
        onClick={accept}
      >
        {t('intake:reviewSection.accept')}
      </Button>
      {isReturning === true ? (
        <ReturnIntakeDialog
          isSubmitting={returnMutation.isPending}
          onConfirm={returnForCorrection}
          onCancel={function cancel() {
            setIsReturning(false);
          }}
        />
      ) : null}
    </>
  );
}
